/**
 * FeedbackService — #324
 *
 * Persists alpha-cohort feedback to the Feedback GraphQL model.
 * Used by FeedbackWidget in the top nav.
 *
 * Mirrors EditorialBriefService / SequenceService's class +
 * singleton + injectable-client pattern (CLAUDE.md Rule 20).
 */

import { generateClient } from 'aws-amplify/data';
import { getCurrentUser } from 'aws-amplify/auth';
import logger from '../utils/logger.js';
import { createValidationError } from '../utils/StandardErrorFormat.js';
import {
  getRecentConsoleEntries,
  formatConsoleEntries,
} from '../utils/consoleBuffer.js';

export const VALID_CATEGORIES = Object.freeze(['bug', 'idea', 'question', 'other']);

/** Cap matches the schema comment + protects the AppSync 256kb mutation budget. */
const MAX_MESSAGE_LENGTH = 5000;

export class FeedbackService {
  constructor(client = null) {
    this.logger = logger.child('FeedbackService');
    this.client = client || generateClient();
  }

  /**
   * Submit a piece of feedback. Auto-collects pageUrl / userAgent /
   * createdAt / userId / appVersion so the widget only has to pass
   * { category, message }.
   *
   * @returns {Promise<{ id: string, createdAt: string }>}
   */
  async submitFeedback({ category, message } = {}) {
    if (!VALID_CATEGORIES.includes(category)) {
      createValidationError(
        `category must be one of: ${VALID_CATEGORIES.join(', ')}`,
        { code: 'CATEGORY_INVALID' }
      );
    }

    const trimmed = typeof message === 'string' ? message.trim() : '';
    if (!trimmed) {
      createValidationError('message is required', { code: 'MESSAGE_REQUIRED' });
    }
    if (trimmed.length > MAX_MESSAGE_LENGTH) {
      createValidationError(
        `message is too long (max ${MAX_MESSAGE_LENGTH} characters)`,
        { code: 'MESSAGE_TOO_LONG' }
      );
    }

    let userId;
    try {
      const user = await getCurrentUser();
      userId = user?.userId;
    } catch (err) {
      this.logger.warn('getCurrentUser failed during feedback submit:', err);
      createValidationError('Please sign in before sending feedback', {
        code: 'NOT_SIGNED_IN',
      });
    }
    if (!userId) {
      createValidationError('Please sign in before sending feedback', {
        code: 'NOT_SIGNED_IN',
      });
    }

    const ts = new Date().toISOString();

    // #328 — for bug submissions, append the last ~30 console.error/.warn
    // entries so triage isn't blind. Already redacted at capture time.
    let enrichedMessage = trimmed;
    if (category === 'bug') {
      const tail = formatConsoleEntries(getRecentConsoleEntries());
      if (tail) {
        enrichedMessage = `${trimmed}\n\n--- Recent console output ---\n\`\`\`\n${tail}\n\`\`\``;
      }
    }

    const payload = {
      userId,
      category,
      message: enrichedMessage,
      pageUrl: typeof window !== 'undefined' ? window.location?.href || null : null,
      userAgent:
        typeof navigator !== 'undefined' ? navigator.userAgent || null : null,
      appVersion:
        (typeof import.meta !== 'undefined' &&
          import.meta.env?.VITE_APP_VERSION) ||
        null,
      status: 'new',
      createdAt: ts,
    };

    const { data, errors } = await this.client.models.Feedback.create(payload);
    // Amplify v6's model client does NOT reject the promise on GraphQL
    // errors — it returns { data: null, errors: [...] }. The widget's
    // submit handler relies on the promise rejecting to show the error
    // alert + preserve the draft text. Surface errors as a thrown
    // Error so the UI's try/catch path is reached.
    //
    // Caught 2026-05-24 by tests/e2e/feedback-widget.spec.js error-path
    // test — pre-Amplify-recovery the missing Feedback model caused
    // client.models.Feedback to be undefined, which DID throw natively
    // and masked this latent bug. Post-recovery the model is deployed
    // and the silent-swallow surfaced.
    if (errors && errors.length > 0) {
      const messages = errors.map((e) => e.message || String(e)).join('; ');
      this.logger.warn('Feedback submit returned GraphQL errors:', errors);
      const err = new Error(`Could not send feedback: ${messages}`);
      err.code = 'FEEDBACK_GRAPHQL_ERRORS';
      err.errors = errors;
      throw err;
    }
    return { id: data?.id, createdAt: data?.createdAt };
  }

  /**
   * Read the current user's past feedback. Returned newest-first.
   * For a future "View my feedback" page; not used by the MVP widget.
   */
  async listMyFeedback() {
    const user = await getCurrentUser();
    const userId = user?.userId;
    if (!userId) return [];
    const { data } = await this.client.models.Feedback.list({
      filter: { userId: { eq: userId } },
    });
    return (data || []).sort((a, b) =>
      (b.createdAt || '').localeCompare(a.createdAt || '')
    );
  }
}

const instance = new FeedbackService();
export { instance as feedbackServiceInstance };
export default instance;
